import { t } from '@if/core/i18n'
import {
  EBeneficiaryType,
  EGender,
  EHomeStatus,
  EJobType,
  ELanguage,
  ELoanPurpose,
  EOtherIncomeType,
  getEGenderList,
  getELanguageList,
  getELoanPurposeList,
} from '@if/core/types'
import * as yup from '@if/core/yup'
import { Constants } from '@src/types/constants'
import { OriginationTracking } from '@src/types/origination'

const nameRegex = /^([-A-ZÀ-Ü]|[a-zà-ü])+([-A-ZÀ-Ü]|[a-zà-ü]|['.\s])*$/i
const specialistRegex = /^([-A-ZÀ-Ü]|[a-zà-ü]|['.\s])*?$/
const civicNumberRegex = /^([0-9]+)([a-zA-Z])?$/

export const AddressSchema = yup.object({
  civicNumber: yup.string().emptyToNull().matches(civicNumberRegex).required().max(10),
  street: yup.string().emptyToNull().required().max(100),
  apartment: yup.string().max(50),
  suite: yup.string().max(10),
  postalCode: yup.string().required().validCanadianPostalCode(),
  city: yup.string().emptyToNull().required().max(50),
  stateIso: yup.string().required(),
  country: yup.string().default('Canada').oneOf(['Canada'], 'The country must be Canada'),
  months: yup.integer().positive().required(),
})

export type Address = yup.InferType<typeof AddressSchema>

export const ExpensesSchema = yup.object({
  homeFeeTypeId: yup.mixed<EHomeStatus>().required().default(EHomeStatus.Renter),
  totalMonthlyHomePayment: yup
    .integer()
    .min(0)
    .default(0)
    .required()
    .when('homeFeeTypeId', {
      is: (val: EHomeStatus) => {
        return val === EHomeStatus.Renter || val === EHomeStatus.OwnWithMortgage
      },
      then: (schema) => schema.positive(),
    }),
  rentMonthly: yup.integer(),
  mortgageMonthly: yup.integer(),
  housingMonthly: yup
    .integer()
    .positive()
    .when('totalMonthlyHomePayment', {
      is: (val: number) => {
        return val > 0
      },
      then: (schema) => schema.required().max(yup.ref('totalMonthlyHomePayment')),
    })
    .when('homeFeeTypeId', {
      is: (val: EHomeStatus) => {
        return val === EHomeStatus.WithRelative
      },
      then: (schema) => schema.min(0).default(0).required(),
    }),
})

export type Expenses = yup.InferType<typeof ExpensesSchema>

export const JobSchema = yup.object({
  jobType: yup.numberEnum<EJobType>().required(),
  employerName: yup
    .string()
    .max(100)
    .when('jobType', {
      is: EJobType.Unemployed,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.emptyToNull().required(),
    }),
  jobTitle: yup.string().when('jobType', {
    is: EJobType.Unemployed,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.emptyToNull().required().max(50),
  }),
  annualSalary: yup.integer().when('jobType', {
    is: EJobType.Unemployed,
    then: (schema) => schema.nullable(),
    otherwise: (schema) =>
      schema
        .typeError(() => t('common.errors.integerValueRequired'))
        .required()
        .min(1),
  }),
  employerPhone: yup.string().when('jobType', {
    is: EJobType.Unemployed,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.validCanadianPhone().required(),
  }),
  employerPhoneExt: yup.string().validPhoneExtension().nullable(),
  months: yup.integer().when('jobType', {
    is: EJobType.Unemployed,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.positive().required(),
  }),
  yearMonths: yup.integer().nullable(),
  years: yup.integer().nullable(),
})

export type ApplicantJob = yup.InferType<typeof JobSchema>

export const OtherIncomeSchema = yup.object({
  jobType: yup.integer().default(EJobType.Unemployed).required(),
  typeId: yup.integer().when('jobType', {
    is: EJobType.Unemployed,
    then: (schema) => schema.required(),
  }),
  annualAmount: yup.integer().when('typeId', {
    is: (value: number | null) => value !== null,
    then: (schema) =>
      schema
        .typeError(() => t('common.errors.integerValueRequired'))
        .required()
        .min(1),
  }),
  description: yup
    .string()
    .nullable()
    .when('typeId', {
      is: EOtherIncomeType.Other,
      then: (schema) => schema.required(),
    }),
})

export type ApplicantOtherIncome = yup.InferType<typeof OtherIncomeSchema>

export const ApplicantSchema = () =>
  yup.object({
    isCoapplicant: yup.boolean().default(false),
    genderId: yup.numberEnum<EGender>(getEGenderList()).required().default(EGender.Men),
    languageId: yup.numberEnum<ELanguage>(getELanguageList()).required().default(0),
    birthDate: yup
      .string()
      .validDate()
      .validAgeRequirement(yup.ref('currentAddress.stateIso'), () => t('common.errors.minimumAgeRequired'))
      .required(),
    firstName: yup.string().matches(nameRegex).required().max(50),
    middleName: yup.string().emptyToNull().nullable().matches(nameRegex).max(50),
    lastName: yup.string().matches(nameRegex).required().max(50),
    sin: yup.string().defined().default('').validSIN(),
    relationWithApplicant: yup.string().when('isCoapplicant', (isCoapplicant, schema: yup.StringSchema) => {
      if (!isCoapplicant[0]) {
        return schema.notRequired().nullable().default(null)
      }
      return schema.required().default(null)
    }),
    cellPhone: yup.string().validCanadianPhone().required(),
    homePhone: yup.string().validCanadianPhone().nullable(),
    email: yup.string().required().email().max(60),
    currentJobs: yup.array(JobSchema).default([JobSchema.getDefault() as unknown as ApplicantJob]),
    otherIncomes: yup.array(OtherIncomeSchema).default([OtherIncomeSchema.getDefault()]),
    currentAddress: AddressSchema.default(AddressSchema.getDefault()),
    spending: ExpensesSchema.default(ExpensesSchema.getDefault()),
    subscribeNewsletter: yup.boolean(),
  })

export type Applicant = yup.InferType<ReturnType<typeof ApplicantSchema>>

export const buildPrequalificationSchemaObject = () => {
  const PrequalificationSchema = yup.object({
    merchantId: yup.string().required(),
    applicant: ApplicantSchema().required().default(ApplicantSchema().getDefault()),
    requestedLoanAmount: yup.integer().required().min(Constants.MinLoanAmount).max(Constants.MaxLoanAmount),
    loanPurposeId: yup
      .mixed<ELoanPurpose | 'homeImprovement' | 'other'>()
      .required()
      .oneOf([...getELoanPurposeList(), 'homeImprovement', 'other']),
    merchantName: yup.string().defined().default(''),
    specialistName: yup.string().nullable().matches(specialistRegex, 'specialistNameErrorMessage'),
    merchantPaymentPlanId: yup.string().nullable(),
    beneficiaryTypeId: yup.integer().default(1),
    otherBeneficiaryFirstName: yup
      .string()
      .trim()
      .when(['loanPurposeId', 'beneficiaryTypeId'], {
        is: (loanPurposeId: ELoanPurpose, beneficiaryTypeId: EBeneficiaryType) =>
          loanPurposeId === ELoanPurpose.Veterinary ||
          loanPurposeId === ELoanPurpose.GoodsAndServices ||
          beneficiaryTypeId !== EBeneficiaryType.Other,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(),
      }),
    otherBeneficiaryLastName: yup
      .string()
      .trim()
      .when(['loanPurposeId', 'beneficiaryTypeId'], {
        is: (loanPurposeId: ELoanPurpose, beneficiaryTypeId: EBeneficiaryType) =>
          loanPurposeId === ELoanPurpose.GoodsAndServices || beneficiaryTypeId !== EBeneficiaryType.Other,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(),
      }),
  })
  return PrequalificationSchema
}
export type Prequalification = yup.InferType<ReturnType<typeof buildPrequalificationSchemaObject>> & {
  origination: OriginationTracking | null
}

const PrequalificationSchema = buildPrequalificationSchemaObject()

export const LoanAmountInputSchema = PrequalificationSchema.pick(['requestedLoanAmount'])

export type LoanAmountInput = yup.InferType<typeof LoanAmountInputSchema>

export const LoanPurposeInputSchema = PrequalificationSchema.pick(['loanPurposeId'])

export const ServiceProviderSchema = PrequalificationSchema.pick([
  'merchantName',
  'merchantId',
  'specialistName',
  'loanPurposeId',
])

export type ServiceProviderInput = yup.InferType<typeof ServiceProviderSchema>

export type LoanPurposeInput = yup.InferType<typeof LoanPurposeInputSchema>

const applicantWithoutContactSchema = () =>
  ApplicantSchema().pick([
    'firstName',
    'middleName',
    'lastName',
    'genderId',
    'languageId',
    'birthDate',
    'sin',
    'relationWithApplicant',
  ])
const beneficiarySchema = PrequalificationSchema.pick([
  'beneficiaryTypeId',
  'otherBeneficiaryFirstName',
  'otherBeneficiaryLastName',
])

export const PersonalInformationInputSchema = () =>
  yup.object({
    applicant: applicantWithoutContactSchema(),
    ...beneficiarySchema.fields,
  })

export type PersonalInformationInput = yup.InferType<ReturnType<typeof PersonalInformationInputSchema>>

export const ContactInformationInputSchema = ApplicantSchema()
  .pick(['cellPhone', 'homePhone', 'email', 'subscribeNewsletter'])
  .shape({
    applicantPhoneNumber: yup.string().validCanadianPhone().optional(),
    cellPhone: yup
      .string()
      .validCanadianPhone()
      .required()
      .test(
        'is-different-from-given-number',
        () => t('contact.coappSameMobileError'),
        (value, ctx) => {
          const applicantPhoneNumber = (ctx as { applicantPhoneNumber?: string })?.applicantPhoneNumber
          return !applicantPhoneNumber || value !== applicantPhoneNumber
        },
      ),
  })

export type ContactInformationInput = yup.InferType<typeof ContactInformationInputSchema>

export const HomeInformationInputSchema = () => ApplicantSchema().pick(['currentAddress', 'spending', 'birthDate'])

export type HomeInformationInput = yup.InferType<ReturnType<typeof HomeInformationInputSchema>>

export const EmploymentInputSchema = ApplicantSchema().pick(['currentJobs', 'otherIncomes'])

export type EmploymentInput = yup.InferType<typeof EmploymentInputSchema>
